import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Trash2, Plus, ChevronDown, ChevronRight,
  Bold, Italic, Underline, Link2,
  GripVertical, ArrowUp, ArrowDown,
} from 'lucide-react';
import { rebuildInlineRefs, renderInlinePreview } from './RichParaEditor';
import { CalsTableEditor } from './CalsTableEditor';
import { CaptionGroupEditor } from './CaptionGroupEditor';
import { CrewDrillEditor } from './CrewEditors';
import { HotspotEditor } from './HotspotEditor';

const uid = () => Math.random().toString(36).substring(2, 10);

const autoGrow = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight + 2, 600) + 'px';
};

/* Definition List editor (used in para and figure legend) */
const DefinitionListEditor = ({ dl, onChange, onRemove, testId }) => {
  const items = dl.items || [];
  return (
    <div className="ml-3 border-l-2 border-emerald-200 pl-3 space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700">definitionList</Badge>
        <button onClick={onRemove} className="p-1 text-slate-300 hover:text-red-500" title="Remove definition list"><Trash2 className="w-3 h-3" /></button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 group/dli">
          <Input
            value={item.term || ''}
            onChange={e => { const next = [...items]; next[i] = { ...next[i], term: e.target.value }; onChange({ ...dl, items: next }); }}
            placeholder="Term (listItemTerm)"
            className="text-sm h-8 w-1/3 shrink-0"
            data-testid={`${testId}-term-${i}`}
          />
          <Textarea
            ref={autoGrow}
            value={item.def || ''}
            onChange={e => { const next = [...items]; next[i] = { ...next[i], def: e.target.value }; onChange({ ...dl, items: next }); }}
            onInput={e => autoGrow(e.target)}
            rows={1}
            placeholder="Definition (listItemDefinition)... Press Enter for a new para"
            className="text-sm resize-none overflow-hidden min-h-[32px] flex-1"
            data-testid={`${testId}-def-${i}`}
          />
          <button onClick={() => onChange({ ...dl, items: items.filter((_, idx) => idx !== i) })}
            className="p-1 mt-1 text-slate-300 hover:text-red-500 opacity-0 group-hover/dli:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-emerald-600"
        onClick={() => onChange({ ...dl, items: [...items, { term: '', def: '' }] })}
        data-testid={`${testId}-add-item`}>
        <Plus className="w-3 h-3" /> definitionListItem
      </Button>
    </div>
  );
};

/* Attention Random List editor (shared by warning/caution/note/seq-list-items) */
const AttnRandomListEditor = ({ items, onChange, onRemove, testId }) => (
  <div className="ml-3 border-l-2 border-current/20 pl-3 space-y-1.5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <Badge variant="outline" className="text-[10px] bg-white/60">attentionRandomList</Badge>
      <button onClick={onRemove} className="p-1 hover:opacity-70" title="Remove list"><Trash2 className="w-3 h-3" /></button>
    </div>
    {(items || []).map((item, i) => (
      <div key={i} className="flex items-center gap-2 group">
        <span className="text-xs opacity-60 w-3 shrink-0">•</span>
        <Input
          value={item}
          onChange={e => { const next = [...items]; next[i] = e.target.value; onChange(next); }}
          placeholder="List item..."
          className="text-sm h-8 bg-white/80"
        />
        <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          className="p-1 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"><Trash2 className="w-3 h-3" /></button>
      </div>
    ))}
    <Button variant="ghost" size="sm" className="text-xs h-6 gap-1" onClick={() => onChange([...(items || []), ''])} data-testid={`${testId}-add-item`}>
      <Plus className="w-3 h-3" /> Add item
    </Button>
  </div>
);

/* ═══════════════════════════════════════════
   ELEMENT EDITOR (para, randomList, etc.)
   ═══════════════════════════════════════════ */
export const ElementEditor = ({ element, onUpdate, onDelete, dmlEntries = [], dirFiles = [], dirIcns = [], internalTargets = [], irttOptions = [] }) => {
  const paraRef = useRef(null);
  const [showRefInput, setShowRefInput] = useState(null);
  const [refDisplay, setRefDisplay] = useState('');
  const [refCode, setRefCode] = useState('');
  const [refTargetType, setRefTargetType] = useState('');
  const wrapSelection = (prefix, suffix) => {
    const ta = paraRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = element.content || '';
    const selected = text.substring(start, end) || 'text';
    const wrapped = `${prefix}${selected}${suffix}`;
    const newContent = text.substring(0, start) + wrapped + text.substring(end);
    const newRefs = rebuildInlineRefs(newContent, element.inlineRefs);
    onUpdate({ ...element, content: newContent, inlineRefs: newRefs.length > 0 ? newRefs : undefined });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + wrapped.length, start + wrapped.length); }, 0);
  };

  // Reference insertion after filling form
  const doInsertRef = () => {
    if (!showRefInput) return;
    const ta = paraRef.current;
    const pos = ta ? ta.selectionStart : (element.content || '').length;
    const text = element.content || '';
    if (showRefInput === 'intRef') {
      if (!refCode) return;
      const marker = `[Ref: ${refCode}]`;
      const newContent = text.substring(0, pos) + marker + text.substring(pos);
      const refData = { type: 'internalRef', refId: refCode, targetType: refTargetType || (irttOptions[0]?.value || 'irtt01') };
      const newRefs = rebuildInlineRefs(newContent, [...(element.inlineRefs || []), refData]);
      onUpdate({ ...element, content: newContent, inlineRefs: newRefs.length > 0 ? newRefs : undefined });
      setShowRefInput(null); setRefDisplay(''); setRefCode(''); setRefTargetType('');
      return;
    }
    const display = refDisplay || (showRefInput === 'dmRef' ? 'Data Module' : showRefInput === 'pmRef' ? 'Publication' : 'External Ref');
    const label = showRefInput === 'dmRef' ? 'DMRef' : showRefInput === 'pmRef' ? 'PMRef' : 'ExtRef';
    const marker = `[${label}: ${display}]`;
    const newContent = text.substring(0, pos) + marker + text.substring(pos);
    let refData;
    if (showRefInput === 'dmRef') refData = { type: 'dmRef', ref: refCode || '', techName: display, infoName: '' };
    else if (showRefInput === 'pmRef') refData = { type: 'pmRef', ref: refCode || '', title: display };
    else refData = { type: 'externalPubRef', title: display, code: refCode || '' };
    const existingRefs = [...(element.inlineRefs || []), refData];
    const newRefs = rebuildInlineRefs(newContent, existingRefs);
    onUpdate({ ...element, content: newContent, inlineRefs: newRefs.length > 0 ? newRefs : undefined });
    setShowRefInput(null); setRefDisplay(''); setRefCode('');
  };

  // Sync inlineRefs on content change
  const handleParaChange = (e) => {
    const newContent = e.target.value;
    const newRefs = rebuildInlineRefs(newContent, element.inlineRefs);
    onUpdate({ ...element, content: newContent, inlineRefs: newRefs.length > 0 ? newRefs : undefined });
  };

  if (element.type === 'para') {
    const hasInlineList = !!element.inlineList;
    const listType = element.inlineList?.type || 'randomList';
    const listItems = element.inlineList?.items || [];

    const addInlineList = (type) => {
      onUpdate({ ...element, inlineList: { type, items: [''] } });
    };
    const removeInlineList = () => {
      onUpdate({ ...element, inlineList: null });
    };
    const updateListItem = (i, val) => {
      const items = [...listItems];
      items[i] = val;
      onUpdate({ ...element, inlineList: { ...element.inlineList, items } });
    };
    const removeListItem = (i) => {
      const items = listItems.filter((_, idx) => idx !== i);
      onUpdate({ ...element, inlineList: items.length > 0 ? { ...element.inlineList, items } : null });
    };
    const addListItem = () => {
      onUpdate({ ...element, inlineList: { ...element.inlineList, items: [...listItems, ''] } });
    };

    return (
      <div className="space-y-2 group" data-testid={`elem-para-${element.id}`}>
        {/* Inline formatting toolbar */}
        <div className="flex items-center gap-1 flex-wrap">
          <Badge variant="outline" className="text-[10px] shrink-0 bg-slate-50">para</Badge>
          <div className="flex items-center gap-0.5 ml-1 border border-slate-200 rounded px-1 py-0.5 bg-white" data-testid={`para-toolbar-${element.id}`}>
            <button onClick={() => wrapSelection('**', '**')} className="px-1.5 py-0.5 rounded text-xs font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors" title="Bold (em01)" data-testid={`btn-bold-${element.id}`}>
              <Bold className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => wrapSelection('*', '*')} className="px-1.5 py-0.5 rounded text-xs italic text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors" title="Italic (em02)" data-testid={`btn-italic-${element.id}`}>
              <Italic className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => wrapSelection('__', '__')} className="px-1.5 py-0.5 rounded text-xs underline text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors" title="Underline (em03)" data-testid={`btn-underline-${element.id}`}>
              <Underline className="w-3.5 h-3.5" />
            </button>
            <span className="w-px h-4 bg-slate-200 mx-0.5" />
            <button onClick={() => { setShowRefInput(showRefInput === 'dmRef' ? null : 'dmRef'); setRefDisplay(''); setRefCode(''); }} className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${showRefInput === 'dmRef' ? 'bg-violet-100 text-violet-700' : 'text-violet-600 hover:bg-violet-50'}`} title="Insert DM Reference" data-testid={`btn-dmref-${element.id}`}>
              dmRef
            </button>
            <button onClick={() => { setShowRefInput(showRefInput === 'pmRef' ? null : 'pmRef'); setRefDisplay(''); setRefCode(''); }} className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${showRefInput === 'pmRef' ? 'bg-blue-100 text-blue-700' : 'text-blue-600 hover:bg-blue-50'}`} title="Insert PM Reference" data-testid={`btn-pmref-${element.id}`}>
              pmRef
            </button>
            <button onClick={() => { setShowRefInput(showRefInput === 'extRef' ? null : 'extRef'); setRefDisplay(''); setRefCode(''); }} className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${showRefInput === 'extRef' ? 'bg-amber-100 text-amber-700' : 'text-amber-600 hover:bg-amber-50'}`} title="Insert External Pub Reference" data-testid={`btn-extref-${element.id}`}>
              extPubRef
            </button>
            <button onClick={() => { setShowRefInput(showRefInput === 'intRef' ? null : 'intRef'); setRefDisplay(''); setRefCode(''); setRefTargetType(''); }} className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${showRefInput === 'intRef' ? 'bg-teal-100 text-teal-700' : 'text-teal-600 hover:bg-teal-50'}`} title="Insert Internal Reference (figure, table, para or step in this DM)" data-testid={`btn-intref-${element.id}`}>
              intRef
            </button>
          </div>
          <div className="flex-1" />
          <button onClick={onDelete} className="p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Reference insertion form */}
        {showRefInput && (
          <div className={`ml-8 p-2.5 rounded-lg border space-y-2 ${showRefInput === 'dmRef' ? 'bg-violet-50 border-violet-200' : showRefInput === 'pmRef' ? 'bg-blue-50 border-blue-200' : showRefInput === 'intRef' ? 'bg-teal-50 border-teal-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`ref-input-form-${element.id}`}>
            <div className="flex items-center gap-2">
              <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-xs font-semibold text-slate-600">Insert {showRefInput === 'dmRef' ? 'DM Reference' : showRefInput === 'pmRef' ? 'PM Reference' : showRefInput === 'intRef' ? 'Internal Reference' : 'External Pub Reference'}</span>
            </div>

            {/* Internal Reference — targets within this data module */}
            {showRefInput === 'intRef' && (
              <>
                {internalTargets.length > 0 ? (
                  <Select onValueChange={(val) => {
                    const t = internalTargets[parseInt(val, 10)];
                    if (t) { setRefCode(t.id); setRefTargetType(t.targetType); }
                  }}>
                    <SelectTrigger className="text-xs h-8 bg-white" data-testid={`ref-int-select-${element.id}`}>
                      <SelectValue placeholder="Select target in this data module..." />
                    </SelectTrigger>
                    <SelectContent>
                      {internalTargets.map((t, ti) => (
                        <SelectItem key={ti} value={String(ti)}>
                          <span className="text-xs">{t.label}</span>
                          <span className="ml-2 font-mono text-[10px] text-slate-400">{t.id}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-[10px] text-slate-400 italic">No referenceable targets (figures, tables, paras/steps with ids) found in this DM yet. Enter an id manually below.</p>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    value={refCode}
                    onChange={e => setRefCode(e.target.value)}
                    placeholder="Target id (e.g. fig-0001)"
                    className="text-xs h-7 flex-1 font-mono"
                    data-testid={`ref-int-id-input-${element.id}`}
                    onKeyDown={e => { if (e.key === 'Enter') doInsertRef(); if (e.key === 'Escape') setShowRefInput(null); }}
                  />
                  <Select value={refTargetType} onValueChange={setRefTargetType}>
                    <SelectTrigger className="text-xs h-7 w-[200px] bg-white" data-testid={`ref-int-type-select-${element.id}`}>
                      <SelectValue placeholder="Target type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {irttOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <span className="font-mono text-[10px]">{o.value}</span>
                          <span className="ml-2 text-xs text-slate-500">{o.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* DM Reference dropdown — combines DML entries + working directory files */}
            {showRefInput === 'dmRef' && (() => {
              const seen = new Set();
              const dmOptions = [];
              for (const e of dmlEntries) {
                const dmc = e.dmc_formatted;
                if (dmc && !seen.has(dmc.toUpperCase())) {
                  seen.add(dmc.toUpperCase());
                  dmOptions.push({ value: dmc, label: `${dmc} — ${e.title || ''}` });
                }
              }
              for (const f of (dirFiles || [])) {
                const n = f.name.toUpperCase();
                if (n.startsWith('DMC-') && n.endsWith('.XML')) {
                  const dmcName = f.name.replace(/\.xml$/i, '');
                  if (!seen.has(dmcName.toUpperCase())) {
                    seen.add(dmcName.toUpperCase());
                    dmOptions.push({ value: dmcName, label: dmcName });
                  }
                }
              }
              return dmOptions.length > 0 ? (
                <Select onValueChange={(val) => {
                  const entry = dmlEntries.find(e => e.dmc_formatted === val);
                  setRefDisplay(entry ? (entry.title || entry.dmc_formatted) : val);
                  setRefCode(val);
                }}>
                  <SelectTrigger className="text-xs h-8 bg-white" data-testid={`ref-dml-select-${element.id}`}>
                    <SelectValue placeholder="Select Data Module..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dmOptions.map((opt, oi) => (
                      <SelectItem key={oi} value={opt.value}>
                        <span className="font-mono text-[10px]">{opt.value}</span>
                        {opt.label !== opt.value && <span className="ml-2 text-xs text-slate-500">{opt.label.replace(opt.value + ' — ', '')}</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-[10px] text-slate-400 italic">No data modules found. Add entries in DML Builder or link a working directory.</p>
              );
            })()}

            {/* PM Reference dropdown — PMC files from working directory + DML entries */}
            {showRefInput === 'pmRef' && (() => {
              const pmOptions = [];
              const seen = new Set();
              for (const f of (dirFiles || [])) {
                const n = f.name.toUpperCase();
                if (n.startsWith('PMC-') && n.endsWith('.XML')) {
                  const pmcName = f.name.replace(/\.xml$/i, '');
                  if (!seen.has(pmcName.toUpperCase())) {
                    seen.add(pmcName.toUpperCase());
                    pmOptions.push({ value: f.name, label: pmcName });
                  }
                }
              }
              return pmOptions.length > 0 ? (
                <Select onValueChange={(val) => {
                  const clean = val.replace(/\.xml$/i, '');
                  setRefDisplay(clean); setRefCode(clean);
                }}>
                  <SelectTrigger className="text-xs h-8 bg-white" data-testid={`ref-pmc-select-${element.id}`}>
                    <SelectValue placeholder="Select Publication Module..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pmOptions.map((opt, oi) => (
                      <SelectItem key={oi} value={opt.value}>
                        <span className="font-mono text-[10px]">{opt.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-[10px] text-slate-400 italic">No PMC files found. Link a working directory with PMC files or type manually below.</p>
              );
            })()}

            {/* Manual input fields (auto-filled from dropdown or typed manually) */}
            {showRefInput !== 'intRef' && (
            <div className="flex items-center gap-2">
              <Input
                value={refDisplay}
                onChange={e => setRefDisplay(e.target.value)}
                placeholder={showRefInput === 'dmRef' ? 'Display name (e.g. General Description)' : showRefInput === 'pmRef' ? 'Display name (e.g. Main Publication)' : 'Title (e.g. ASD-STE100)'}
                className="text-xs h-7 flex-1"
                data-testid={`ref-display-input-${element.id}`}
                onKeyDown={e => { if (e.key === 'Enter') doInsertRef(); if (e.key === 'Escape') setShowRefInput(null); }}
              />
              <Input
                value={refCode}
                onChange={e => setRefCode(e.target.value)}
                placeholder={showRefInput === 'dmRef' ? 'DMC code' : showRefInput === 'pmRef' ? 'PMC code' : 'Code (optional)'}
                className="text-xs h-7 w-[260px] font-mono"
                data-testid={`ref-code-input-${element.id}`}
                onKeyDown={e => { if (e.key === 'Enter') doInsertRef(); if (e.key === 'Escape') setShowRefInput(null); }}
              />
            </div>
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" className="h-6 text-xs gap-1" onClick={doInsertRef} data-testid={`ref-insert-btn-${element.id}`}>
                <Plus className="w-3 h-3" /> Insert
              </Button>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowRefInput(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Paragraph textarea */}
        <div className="ml-0">
          <Textarea
            ref={(el) => { paraRef.current = el; autoGrow(el); }}
            value={element.content || ''}
            onChange={handleParaChange}
            onInput={e => autoGrow(e.target)}
            placeholder="Enter paragraph text... Use toolbar for emphasis and references"
            rows={2}
            className="text-sm resize-y flex-1 min-h-[38px] overflow-hidden"
          />
        </div>

        {/* Inline elements preview (emphasis, refs) */}
        {element.inlineRefs && element.inlineRefs.length > 0 && (
          <div className="ml-8 px-3 py-2 rounded bg-slate-50 border border-slate-100 text-sm leading-relaxed" data-testid={`inline-preview-${element.id}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Preview</span>
              {element.inlineRefs.map((r, ri) => (
                <Badge key={ri} variant="outline" className="text-[9px] h-4 px-1">
                  {r.type === 'emphasis' ? `${r.emphasisType}` : r.type}
                </Badge>
              ))}
            </div>
            <div className="text-slate-700">{renderInlinePreview(element.content || '')}</div>
          </div>
        )}

        {/* Inline list nested inside para */}
        {hasInlineList && (
          <div className="ml-8 space-y-1.5 border-l-2 border-blue-200 pl-3" data-testid={`inline-list-${element.id}`}>
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">{listType}</Badge>
              <button onClick={removeInlineList} className="p-1 text-slate-300 hover:text-red-500" title="Remove list">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            {listItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 group/item">
                <span className="text-xs text-slate-400 w-4 shrink-0">{listType === 'sequentialList' ? `${i + 1}.` : '•'}</span>
                <Input
                  value={item}
                  onChange={e => updateListItem(i, e.target.value)}
                  placeholder="List item..."
                  className="text-sm h-8"
                />
                <button
                  onClick={() => removeListItem(i)}
                  className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover/item:opacity-100 transition-opacity"
                ><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            <Button
              variant="ghost" size="sm"
              className="text-xs text-blue-600 hover:text-blue-800 h-7 gap-1"
              onClick={addListItem}
              data-testid={`add-inline-item-${element.id}`}
            ><Plus className="w-3 h-3" /> Add item</Button>
          </div>
        )}

        {/* Definition list nested inside para */}
        {element.definitionList && (
          <div className="ml-8">
            <DefinitionListEditor
              dl={element.definitionList}
              onChange={dl => onUpdate({ ...element, definitionList: dl })}
              onRemove={() => onUpdate({ ...element, definitionList: undefined })}
              testId={`para-deflist-${element.id}`}
            />
          </div>
        )}

        {/* Add inline list buttons */}
        {!hasInlineList && (
          <div className="ml-8 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-blue-500" onClick={() => addInlineList('randomList')}>
              <Plus className="w-2.5 h-2.5" /> randomList
            </Button>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-blue-500" onClick={() => addInlineList('sequentialList')}>
              <Plus className="w-2.5 h-2.5" /> sequentialList
            </Button>
            {!element.definitionList && (
              <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1 text-emerald-600"
                onClick={() => onUpdate({ ...element, definitionList: { items: [{ term: '', def: '' }] } })}
                data-testid={`add-para-deflist-${element.id}`}>
                <Plus className="w-2.5 h-2.5" /> definitionList
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'randomList' || element.type === 'sequentialList') {
    const label = element.type === 'randomList' ? 'randomList' : 'sequentialList';
    return (
      <div className="space-y-2 pl-1" data-testid={`elem-${element.type}-${element.id}`}>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">{label} (standalone)</Badge>
          <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        <div className="space-y-1.5 ml-4 border-l-2 border-blue-200 pl-3">
          {(element.items || []).map((item, i) => (
            <div key={i} className="flex items-center gap-2 group">
              <span className="text-xs text-slate-400 w-4 shrink-0">{element.type === 'sequentialList' ? `${i + 1}.` : '•'}</span>
              <Input
                value={item}
                onChange={e => {
                  const items = [...(element.items || [])];
                  items[i] = e.target.value;
                  onUpdate({ ...element, items });
                }}
                placeholder="List item..."
                className="text-sm h-8"
              />
              <button
                onClick={() => {
                  const items = (element.items || []).filter((_, idx) => idx !== i);
                  onUpdate({ ...element, items });
                }}
                className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              ><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <Button
            variant="ghost" size="sm"
            className="text-xs text-blue-600 hover:text-blue-800 h-7 gap-1"
            onClick={() => onUpdate({ ...element, items: [...(element.items || []), ''] })}
            data-testid={`add-list-item-${element.id}`}
          ><Plus className="w-3 h-3" /> Add item</Button>
        </div>
      </div>
    );
  }

  if (element.type === 'note' || element.type === 'warning' || element.type === 'caution') {
    const colors = {
      note: 'bg-blue-50 border-blue-200 text-blue-700',
      warning: 'bg-red-50 border-red-200 text-red-700',
      caution: 'bg-amber-50 border-amber-200 text-amber-700',
    };
    const isNote = element.type === 'note';
    const paraTag = isNote ? 'notePara' : 'warningAndCautionPara';
    const attnLists = element.attnLists || [];
    return (
      <div className={`p-3 rounded border ${colors[element.type]} space-y-2`} data-testid={`elem-${element.type}-${element.id}`}>
        <div className="flex items-center justify-between">
          <Badge className="text-[10px]">{element.type.toUpperCase()}</Badge>
          <button onClick={onDelete} className="p-1 hover:opacity-70"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        <Textarea
          ref={autoGrow}
          value={element.content || ''}
          onChange={e => onUpdate({ ...element, content: e.target.value })}
          onInput={e => autoGrow(e.target)}
          placeholder={`${element.type} text... Press Enter for a new ${paraTag}`}
          rows={2}
          className="text-sm resize-y bg-white/80 overflow-hidden"
        />
        {(element.content || '').includes('\n') && (
          <p className="text-[10px] opacity-70">{(element.content || '').split('\n').filter(l => l.trim()).length} × &lt;{paraTag}&gt;</p>
        )}

        {/* warning/caution: single attentionRandomList */}
        {!isNote && element.attnRandom && (
          <AttnRandomListEditor
            items={element.attnRandom}
            onChange={items => onUpdate({ ...element, attnRandom: items })}
            onRemove={() => onUpdate({ ...element, attnRandom: undefined })}
            testId={`attn-random-${element.id}`}
          />
        )}
        {!isNote && !element.attnRandom && (
          <Button variant="ghost" size="sm" className="text-xs h-6 gap-1"
            onClick={() => onUpdate({ ...element, attnRandom: [''] })}
            data-testid={`add-attn-random-${element.id}`}>
            <Plus className="w-3 h-3" /> Attention Random List
          </Button>
        )}

        {/* note: attention random + sequential lists */}
        {isNote && attnLists.map((lst, li) => {
          const updateList = (updated) => {
            const next = [...attnLists];
            next[li] = updated;
            onUpdate({ ...element, attnLists: next });
          };
          const removeList = () => {
            const next = attnLists.filter((_, idx) => idx !== li);
            onUpdate({ ...element, attnLists: next.length > 0 ? next : undefined });
          };
          if (lst.kind === 'random') {
            return (
              <AttnRandomListEditor key={lst.id || li}
                items={lst.items || []}
                onChange={items => updateList({ ...lst, items })}
                onRemove={removeList}
                testId={`attn-random-${element.id}-${li}`}
              />
            );
          }
          const seqItems = lst.items || [];
          const hasRandomInSeq = seqItems.some(it => it && it.random);
          return (
            <div key={lst.id || li} className="ml-3 border-l-2 border-current/20 pl-3 space-y-1.5" data-testid={`attn-seq-${element.id}-${li}`}>
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-[10px] bg-white/60">attentionSequentialList</Badge>
                <button onClick={removeList} className="p-1 hover:opacity-70" title="Remove list"><Trash2 className="w-3 h-3" /></button>
              </div>
              {seqItems.map((item, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center gap-2 group">
                    <span className="text-xs opacity-60 w-3 shrink-0">{i + 1}.</span>
                    <Input
                      value={item.text || ''}
                      onChange={e => {
                        const items = [...seqItems];
                        items[i] = { ...items[i], text: e.target.value };
                        updateList({ ...lst, items });
                      }}
                      placeholder="List item..."
                      className="text-sm h-8 bg-white/80"
                    />
                    {!item.random && (
                      <button
                        onClick={() => {
                          const items = [...seqItems];
                          items[i] = { ...items[i], random: [''] };
                          updateList({ ...lst, items });
                        }}
                        disabled={hasRandomInSeq}
                        className="text-[10px] whitespace-nowrap px-1.5 py-0.5 rounded border border-current/30 hover:bg-white/60 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={hasRandomInSeq ? 'Only one attentionRandomList is allowed per attentionSequentialList' : 'Add attentionRandomList to this item'}
                        data-testid={`seq-item-add-random-${element.id}-${li}-${i}`}
                      >+ Random</button>
                    )}
                    <button onClick={() => {
                      const items = seqItems.filter((_, idx) => idx !== i);
                      updateList({ ...lst, items });
                    }} className="p-1 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  {item.random && (
                    <div className="ml-5">
                      <AttnRandomListEditor
                        items={item.random}
                        onChange={rItems => {
                          const items = [...seqItems];
                          items[i] = { ...items[i], random: rItems };
                          updateList({ ...lst, items });
                        }}
                        onRemove={() => {
                          const items = [...seqItems];
                          items[i] = { ...items[i], random: undefined };
                          updateList({ ...lst, items });
                        }}
                        testId={`attn-seq-random-${element.id}-${li}-${i}`}
                      />
                    </div>
                  )}
                </div>
              ))}
              <Button variant="ghost" size="sm" className="text-xs h-6 gap-1"
                onClick={() => updateList({ ...lst, items: [...seqItems, { text: '' }] })}
                data-testid={`attn-seq-add-item-${element.id}-${li}`}>
                <Plus className="w-3 h-3" /> Add item
              </Button>
            </div>
          );
        })}
        {isNote && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" className="text-xs h-6 gap-1"
              onClick={() => onUpdate({ ...element, attnLists: [...attnLists, { id: uid(), kind: 'random', items: [''] }] })}
              data-testid={`add-attn-random-${element.id}`}>
              <Plus className="w-3 h-3" /> Attention Random List
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-6 gap-1"
              onClick={() => onUpdate({ ...element, attnLists: [...attnLists, { id: uid(), kind: 'sequential', items: [{ text: '' }] }] })}
              data-testid={`add-attn-seq-${element.id}`}>
              <Plus className="w-3 h-3" /> Attention Sequential List
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'figure') {
    return (
      <div className="p-3 rounded border border-slate-200 bg-slate-50 space-y-2" data-testid={`elem-figure-${element.id}`}>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-[10px] bg-slate-100">figure</Badge>
          <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        <Input
          value={element.title || ''}
          onChange={e => onUpdate({ ...element, title: e.target.value })}
          placeholder="Figure title..."
          className="text-sm h-8"
        />
        {dirIcns.length > 0 && (
          <select
            className="w-full h-8 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700"
            value={dirIcns.some(x => x.base === element.icn) ? element.icn : ''}
            onChange={e => e.target.value && onUpdate({ ...element, icn: e.target.value })}
            data-testid={`figure-icn-picker-${element.id}`}
          >
            <option value="">Select ICN from working directory… ({dirIcns.length} found)</option>
            {dirIcns.map(x => <option key={x.name} value={x.base}>{x.name}</option>)}
          </select>
        )}
        <Input
          value={element.icn || ''}
          onChange={e => onUpdate({ ...element, icn: e.target.value })}
          placeholder="ICN reference (e.g. ICN-07GB6-CI001-001-01)"
          className="text-sm h-8 font-mono"
        />
        {/* Additional graphics (multiple ICNs per figure) */}
        {(element.icns || []).map((icn, i) => (
          <div key={i} className="flex items-center gap-2 group/gph">
            <Badge variant="outline" className="text-[9px] shrink-0 bg-slate-100 text-slate-500">graphic {i + 2}</Badge>
            {dirIcns.length > 0 ? (
              <select
                className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700 flex-1"
                value={dirIcns.some(x => x.base === icn) ? icn : ''}
                onChange={e => {
                  const next = [...element.icns];
                  next[i] = e.target.value;
                  onUpdate({ ...element, icns: next });
                }}
                data-testid={`figure-extra-icn-picker-${i}-${element.id}`}
              >
                <option value="">{icn || 'Select ICN…'}</option>
                {dirIcns.map(x => <option key={x.name} value={x.base}>{x.name}</option>)}
              </select>
            ) : (
              <Input
                value={icn}
                onChange={e => {
                  const next = [...element.icns];
                  next[i] = e.target.value;
                  onUpdate({ ...element, icns: next });
                }}
                placeholder="ICN reference..."
                className="text-sm h-8 font-mono flex-1"
                data-testid={`figure-extra-icn-${i}-${element.id}`}
              />
            )}
            <button
              onClick={() => onUpdate({ ...element, icns: element.icns.filter((_, idx) => idx !== i) })}
              className="p-1 text-slate-300 hover:text-red-500"
              title="Remove graphic"
            ><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-slate-500"
            onClick={() => onUpdate({ ...element, icns: [...(element.icns || []), ''] })}
            data-testid={`add-figure-graphic-${element.id}`}>
            <Plus className="w-3 h-3" /> Graphic
          </Button>
          {!element.definitionList && (
            <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-emerald-600"
              onClick={() => onUpdate({ ...element, definitionList: { items: [{ term: '', def: '' }] } })}
              data-testid={`add-figure-deflist-${element.id}`}>
              <Plus className="w-3 h-3" /> Definition List
            </Button>
          )}
        </div>
        {element.definitionList && (
          <DefinitionListEditor
            dl={element.definitionList}
            onChange={dl => onUpdate({ ...element, definitionList: dl })}
            onRemove={() => onUpdate({ ...element, definitionList: undefined })}
            testId={`figure-deflist-${element.id}`}
          />
        )}
        <HotspotEditor element={element} onUpdate={onUpdate} dirIcns={dirIcns} internalTargets={internalTargets} irttOptions={irttOptions} dmlEntries={dmlEntries} dirFiles={dirFiles} />
      </div>
    );
  }

  if (element.type === 'table') {
    return (
      <CalsTableEditor
        element={element}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    );
  }

  if (element.type === 'captionGroup') {
    return (
      <CaptionGroupEditor
        element={element}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    );
  }

  if (element.type === 'crewDrill') {
    return (
      <CrewDrillEditor
        drill={element.drill || { id: element.id, title: '', children: [] }}
        index={0}
        onUpdate={(d) => onUpdate({ ...element, drill: d })}
        onDelete={onDelete}
        dmlEntries={dmlEntries}
        dirFiles={dirFiles}
        dirIcns={dirIcns}
        internalTargets={internalTargets}
      />
    );
  }

  return null;
};

/* ═══════════════════════════════════════════
   LEVELLED PARA BLOCK (also serves as proceduralStep)
   ═══════════════════════════════════════════ */
const _registry = {};

function LpElementRow({ elem, i, depth, isCrew, onUpdate, onDelete, dmlEntries, dirFiles, dirIcns, internalTargets, irttOptions, allocateId }) {
  const NestedLp = _registry.LevelledParaBlock;
  if (elem.type === 'levelledPara') {
    return (
      <NestedLp
        lp={elem.lp || { id: elem.id, title: '', elements: [] }}
        index={i}
        depth={depth + 1}
        onUpdate={(lp2) => onUpdate(i, { ...elem, lp: lp2, id: lp2.id || elem.id })}
        onDelete={() => onDelete(i)}
        isProcedural={false}
        isCrew={isCrew}
        dmlEntries={dmlEntries}
        dirFiles={dirFiles}
        dirIcns={dirIcns}
        internalTargets={internalTargets}
        irttOptions={irttOptions}
        allocateId={allocateId}
      />
    );
  }
  return (
    <ElementEditor
      element={elem}
      onUpdate={(updated) => onUpdate(i, updated)}
      onDelete={() => onDelete(i)}
      dmlEntries={dmlEntries}
      dirFiles={dirFiles}
      dirIcns={dirIcns}
      internalTargets={internalTargets}
      irttOptions={irttOptions}
    />
  );
};

export function LevelledParaBlock({ lp, index, onUpdate, onDelete, isProcedural, isCrew = false, depth = 0, dmlEntries, dirFiles, dirIcns = [], internalTargets = [], irttOptions = [], allocateId = null, onActive = null, collapsed: collapsedProp, onToggleCollapse, dragHandleProps = null, onMoveUp = null, onMoveDown = null, count = 0 }) {
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = onToggleCollapse ? !!collapsedProp : localCollapsed;
  const toggleCollapsed = () => (onToggleCollapse ? onToggleCollapse() : setLocalCollapsed(!localCollapsed));
  const blockLabel = isProcedural ? 'proceduralStep' : 'levelledPara';
  const stepBadge = isProcedural ? `Step ${index + 1}` : `L${depth + 1}`;
  const hasDrill = (lp.elements || []).some(e => e.type === 'crewDrill');
  // S1000D ordering: warnings first, then cautions, then other content
  const _els = lp.elements || [];
  const warningAllowed = _els.every(e => e.type === 'warning');
  const cautionAllowed = _els.every(e => e.type === 'warning' || e.type === 'caution');

  // descript.xsd: nested levelledParas must come after all other content — keep editor order matching the XML
  const normalizeOrder = (elements) => {
    const content = elements.filter(e => e.type !== 'levelledPara');
    const nested = elements.filter(e => e.type === 'levelledPara');
    return [...content, ...nested];
  };

  const addElement = (type) => {
    let newElem;
    if (type === 'figure') {
      newElem = { id: allocateId ? allocateId('fig') : uid(), type: 'figure', title: '', icn: '' };
    } else if (type === 'table') {
      newElem = { id: allocateId ? allocateId('tab') : uid(), type: 'table', title: '', cols: 3, colspecs: [{ colname: 'col1', colwidth: '1*' }, { colname: 'col2', colwidth: '1*' }, { colname: 'col3', colwidth: '1*' }], thead: [['', '', '']], tbody: [['', '', '']] };
    } else if (type === 'captionGroup') {
      newElem = { id: allocateId ? allocateId('cgp') : uid(), type: 'captionGroup', cols: 2, applicRefId: '', colspecs: [{ colnum: '1', colname: 'col1', colwidth: '30mm' }, { colnum: '2', colname: 'col2', colwidth: '30mm' }], rows: [[{ colname: 'col1', color: 'co04', captionWidth: '30mm', text: '' }, { colname: 'col2', color: 'co51', captionWidth: '30mm', text: '' }]] };
    } else if (type === 'crewDrill') {
      newElem = { id: uid(), type: 'crewDrill', drill: { id: uid(), title: '', children: [] } };
    } else if (type === 'levelledPara') {
      const nid = allocateId ? allocateId('par') : uid();
      newElem = { id: nid, type: 'levelledPara', lp: { id: nid, title: '', elements: [] } };
    } else {
      newElem = { id: uid(), type, content: '', items: type.includes('List') ? [''] : undefined };
    }
    onUpdate({ ...lp, elements: normalizeOrder([...(lp.elements || []), newElem]) });
  };

  const updateElement = (idx, elem) => {
    const elements = [...(lp.elements || [])];
    elements[idx] = elem;
    onUpdate({ ...lp, elements });
  };

  const deleteElement = (idx) => {
    onUpdate({ ...lp, elements: (lp.elements || []).filter((_, i) => i !== idx) });
  };

  const dragElemIdx = useRef(null);
  const moveElement = (from, to) => {
    const elements = [...(lp.elements || [])];
    if (from === to || from < 0 || to < 0 || to >= elements.length) return;
    const [m] = elements.splice(from, 1);
    elements.splice(to, 0, m);
    onUpdate({ ...lp, elements: normalizeOrder(elements) });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`levelled-para-${index}`} onFocusCapture={onActive || undefined} onClickCapture={onActive || undefined}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-t-lg border-b border-slate-200">
        <div className="flex items-center gap-1 min-w-0">
          {dragHandleProps && (
            <span {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0" title="Drag to reorder" data-testid={`drag-step-${index}`}>
              <GripVertical className="w-4 h-4" />
            </span>
          )}
          <button onClick={toggleCollapsed} className="flex items-center gap-2 text-sm font-medium text-slate-700 min-w-0">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <Badge className={`text-[10px] ${isProcedural ? 'bg-teal-100 text-teal-700' : 'bg-violet-100 text-violet-700'}`}>{stepBadge}</Badge>
            {blockLabel}
            {lp.title && <span className="text-slate-400 font-normal ml-2 truncate max-w-[200px]">{lp.title}</span>}
          </button>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {onMoveUp && (
            <button onClick={onMoveUp} disabled={index === 0} className="p-1.5 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent" title="Move up" data-testid={`move-step-up-${index}`}>
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          )}
          {onMoveDown && (
            <button onClick={onMoveDown} disabled={index >= count - 1} className="p-1.5 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent" title="Move down" data-testid={`move-step-down-${index}`}>
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Title (optional)</Label>
            <Input
              value={lp.title || ''}
              onChange={e => onUpdate({ ...lp, title: e.target.value })}
              placeholder="Section heading..."
              className="text-sm font-medium"
              data-testid={`lp-title-${index}`}
            />
          </div>

          {/* Elements */}
          <div className="space-y-3">
            {(lp.elements || []).map((elem, i) => (
              <div
                key={elem.id || i}
                className="flex items-start gap-1 group/elemrow"
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault(); e.stopPropagation();
                  if (dragElemIdx.current !== null && dragElemIdx.current !== i) moveElement(dragElemIdx.current, i);
                  dragElemIdx.current = null;
                }}
                data-testid={`elem-row-${index}-${i}`}
              >
                {(lp.elements || []).length > 1 && (
                  <div className="flex flex-col items-center pt-1 shrink-0 opacity-0 group-hover/elemrow:opacity-100 transition-opacity">
                    <span
                      draggable
                      onDragStart={e => { dragElemIdx.current = i; e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
                      className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500"
                      title="Drag to reorder"
                      data-testid={`drag-elem-${index}-${i}`}
                    ><GripVertical className="w-3.5 h-3.5" /></span>
                    <button onClick={() => moveElement(i, i - 1)} disabled={i === 0} className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30" title="Move up" data-testid={`move-elem-up-${index}-${i}`}>
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => moveElement(i, i + 1)} disabled={i >= (lp.elements || []).length - 1} className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30" title="Move down" data-testid={`move-elem-down-${index}-${i}`}>
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <LpElementRow elem={elem} i={i} depth={depth} isCrew={isCrew} onUpdate={updateElement} onDelete={deleteElement} dmlEntries={dmlEntries} dirFiles={dirFiles} dirIcns={dirIcns} internalTargets={internalTargets} irttOptions={irttOptions} allocateId={allocateId} />
                </div>
              </div>
            ))}
          </div>

          {/* Add element buttons */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-dashed border-slate-200">
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addElement('para')}>
              <Plus className="w-3 h-3" /> Para
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addElement('randomList')}>
              <Plus className="w-3 h-3" /> Random List
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addElement('sequentialList')}>
              <Plus className="w-3 h-3" /> Sequential List
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addElement('note')}>
              <Plus className="w-3 h-3" /> Note
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500 disabled:opacity-40" disabled={!warningAllowed}
              title={warningAllowed ? '' : `A warning must be the first element of the ${blockLabel}`}
              onClick={() => addElement('warning')} data-testid={`add-warning-elem-${index}`}>
              <Plus className="w-3 h-3" /> Warning
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500 disabled:opacity-40" disabled={!cautionAllowed}
              title={cautionAllowed ? '' : `A caution can only follow warnings at the start of the ${blockLabel}`}
              onClick={() => addElement('caution')} data-testid={`add-caution-elem-${index}`}>
              <Plus className="w-3 h-3" /> Caution
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addElement('figure')}>
              <Plus className="w-3 h-3" /> Figure
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addElement('table')} data-testid={`add-table-elem-${index}`}>
              <Plus className="w-3 h-3" /> Table
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-emerald-600" onClick={() => addElement('captionGroup')} data-testid={`add-caption-elem-${index}`}>
              <Plus className="w-3 h-3" /> captionGroup
            </Button>
            {!isProcedural && (
              depth < 3 ? (
                <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-indigo-600" onClick={() => addElement('levelledPara')} data-testid={`add-nested-lp-elem-${index}`}>
                  <Plus className="w-3 h-3" /> levelledPara
                </Button>
              ) : (
                <span className="text-[10px] text-slate-400 self-center" data-testid={`lp-depth-limit-hint-${index}`}>
                  max 4 levelledPara levels reached
                </span>
              )
            )}
            {isCrew && (
              <>
                <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-violet-600 disabled:opacity-40" disabled={hasDrill} onClick={() => addElement('crewDrill')} data-testid={`add-crewdrill-elem-${index}`}>
                  <Plus className="w-3 h-3" /> crewDrill
                </Button>
                {hasDrill && (
                  <span className="text-[10px] text-slate-400 self-center" data-testid={`crewdrill-limit-hint-${index}`}>
                    crew.xsd allows one crewDrill per levelledPara — add a nested levelledPara for another drill
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

_registry.LevelledParaBlock = LevelledParaBlock;
